<template>
  <div class="LLTextarea" :class="inputClasses">
    <LLInputCaption v-if="showCaption && !isExpanded" :disabled="disabled"
      ><slot name="caption"></slot
    ></LLInputCaption>
    <div class="LLTextarea__container">
      <div v-if="isExpanded" class="LLTextarea__expanded-header">
        <div class="LLTextarea__expanded-header-text">
          <span v-if="expandedTitle" class="LLTextarea__expanded-title">{{ expandedTitle }}</span>
          <span v-if="expandedSubtitle" class="LLTextarea__expanded-subtitle">{{ expandedSubtitle }}</span>
        </div>
        <LLButton darkned small class="LLTextarea__collapse-button" @click="collapse">
          <template #icon-left><ChevronDownIcon /></template>
          {{ $t('button_collapse') }}
        </LLButton>
      </div>
      <div class="LLTextarea__header">
        <LLTextareaMenuBar
          v-if="!hideMenuBar && editor"
          :disabled="disabled"
          class="LLTextarea__top-menubar"
          :editor="editor"
        >
          <template #additional-actions> <slot name="additional-actions"></slot> </template>
        </LLTextareaMenuBar>
        <LLPopper
          v-if="!hideEmoji"
          :show.sync="menuStates.emojiTop"
          :offset="[0, 0]"
          tooltip-white
          :disabled="disabled"
          placement="top-start"
        >
          <template #trigger>
            <LLButton :disabled="disabled" :active="menuStates.emojiTop" tertiary small>
              <template #icon-left>
                <EmojiIcon />
              </template>
            </LLButton>
          </template>
          <template #tooltip>
            <LLTextareaEmoji @select="onEmojiSelect" />
          </template>
        </LLPopper>
      </div>
      <div class="LLTextarea__input-wrapper">
        <!-- 'below' composer mode (#1684 requirement 5): attached files show as chips
             above the text, not below it - matches the prototype's attach-strip. -->
        <div
          v-if="isActionsBelow && showBottomContent"
          class="LLTextarea__additional-content LLTextarea__additional-content_above"
        >
          <slot name="bottom-content"></slot>
        </div>
        <div class="LLTextarea__input-content" :style="inputStyle">
          <div ref="editorWrapper" class="LLTextarea__editor-wrapper" :style="editorWrapperStyle">
            <EditorContent ref="editor-content" class="LLTextarea__editor" :editor="editor"></EditorContent>
          </div>
          <div
            v-if="isActionsBelow && dynamicSize && !isExpanded"
            class="LLTextarea__resize-handle LLTextarea__resize-handle_below"
            @mousedown="onResizeStart"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 1L1 9M9 5L5 9" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
            </svg>
          </div>
          <div v-if="isActionsInline" class="LLTextarea__actions" @click="onActionsClick">
            <slot v-if="hideMenuBar" name="additional-actions"></slot>
            <LLPopper
              v-if="!hideMenuBar"
              :show.sync="menuStates.menuBar"
              :offset="[0, 0]"
              :disabled="disabled"
              class="LLTextarea__action"
              tooltip-white
              placement="top-start"
            >
              <template #trigger>
                <LLButton :disabled="disabled" :active="menuStates.menuBar" tertiary small>
                  <template #icon-left>
                    <TextareaMenuBarIcon></TextareaMenuBarIcon>
                  </template>
                </LLButton>
              </template>
              <template #tooltip>
                <LLTextareaMenuBar v-if="editor" class="LLTextarea__popup-menubar" :editor="editor">
                  <template #additional-actions> <slot name="additional-actions"></slot> </template>
                </LLTextareaMenuBar>
              </template>
            </LLPopper>
            <LLPopper
              v-if="!hideEmoji"
              :show.sync="menuStates.emoji"
              :disabled="disabled"
              :offset="[0, 0]"
              class="LLTextarea__action"
              tooltip-white
              placement="top-start"
            >
              <template #trigger>
                <LLButton :disabled="disabled" :active="menuStates.emoji" tertiary small>
                  <template #icon-left>
                    <EmojiIcon />
                  </template>
                </LLButton>
              </template>
              <template #tooltip>
                <LLTextareaEmoji @select="onEmojiSelect" />
              </template>
            </LLPopper>
          </div>
        </div>
        <div v-if="!isActionsBelow && showBottomContent" class="LLTextarea__additional-content">
          <slot name="bottom-content"></slot>
        </div>
        <!--
          "below" layout: one full-width toolbar row under the text (and under any file chips),
          holding the same Aa/emoji poppers plus whatever the caller puts in `additional-actions`
          (media upload, Send, an Anonymous checkbox, ...). Used by composers redesigned per #1684
          so the editor itself can take the full response width instead of leaving a side gutter
          for these icons.
        -->
        <div
          v-if="isActionsBelow"
          class="LLTextarea__actions LLTextarea__actions_below"
          @click="onActionsClick"
        >
          <LLPopper
            v-if="!hideMenuBar"
            :show.sync="menuStates.menuBar"
            :offset="[0, 0]"
            :disabled="disabled"
            class="LLTextarea__action"
            tooltip-white
            placement="top-start"
          >
            <template #trigger>
              <LLButton :disabled="disabled" :active="menuStates.menuBar" tertiary>
                <template #icon-left>
                  <span class="LLTextarea__aa-label">Aa</span>
                </template>
              </LLButton>
            </template>
            <template #tooltip>
              <LLTextareaMenuBar v-if="editor" class="LLTextarea__popup-menubar" :editor="editor" />
            </template>
          </LLPopper>
          <LLPopper
            v-if="!hideEmoji"
            :show.sync="menuStates.emoji"
            :disabled="disabled"
            :offset="[0, 0]"
            class="LLTextarea__action"
            tooltip-white
            placement="top-start"
          >
            <template #trigger>
              <LLButton :disabled="disabled" :active="menuStates.emoji" tertiary>
                <template #icon-left>
                  <!-- Outline style (not the shared, filled EmojiIcon used by every other
                       LLTextarea mode) to match the prototype exactly - scoped to this
                       'below' composer trigger only, same reasoning as the Aa label above
                       - see #1684. -->
                  <svg class="LLTextarea__emoji-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
                    <path
                      d="M8.5 14.5c.9 1.2 2.1 1.8 3.5 1.8s2.6-.6 3.5-1.8"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                    />
                    <circle cx="9" cy="10" r="0.6" fill="currentColor" />
                    <circle cx="15" cy="10" r="0.6" fill="currentColor" />
                  </svg>
                </template>
              </LLButton>
            </template>
            <template #tooltip>
              <LLTextareaEmoji @select="onEmojiSelect" />
            </template>
          </LLPopper>
          <slot name="additional-actions" :is-capped="isCapped" :expand="expand"></slot>
        </div>
        <div v-if="isActionsBelow && !isExpanded" class="LLTextarea__hint">
          {{ $t('composer_send-hint') }}
        </div>
      </div>
      <LLPopper
        v-if="(hasError || showError) && showErrorInTooltip"
        :show="editorStates.isFocused"
        tooltip-red
        tooltip-with-arrow
        placement="bottom-end"
        :offset="[0, 5]"
        trigger="none"
      >
        <template #trigger></template>
        <template #tooltip>
          <slot name="error"></slot>
          <template v-if="hasError">{{ errorsString }}</template>
        </template>
      </LLPopper>
      <LLInputError v-if="(hasError || showError) && !showErrorInTooltip">
        <div v-if="hasError" class="LLTextInputNew__error LLTextInputNew__styling-error-color">
          <slot name="error"></slot>
          <template v-if="hasError">{{ errorsString }}</template>
        </div>
      </LLInputError>
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'

import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import Underline from '@tiptap/extension-underline'
import BulletList from '@tiptap/extension-bullet-list'
import ListItem from '@tiptap/extension-list-item'
import Link from '@tiptap/extension-link'
import HardBreak from '@tiptap/extension-hard-break'
import Paragraph from '@tiptap/extension-paragraph'
import Document from '@tiptap/extension-document'
import OrderedList from '@tiptap/extension-ordered-list'
import Text from '@tiptap/extension-text'
import Placeholder from '@tiptap/extension-placeholder'
import { isNumber } from 'lodash'
import TextareaMenuBarIcon from '../../assets/icons/TextareaMenuBarIcon'
import emojiToHtml from '../../utils/emojiToHtml'
import EmojiImage from '@/utils/tiptap/EmojiImage'
import Mention from '@/utils/tiptap/Mention'

import LLButton from '@/components/common/ui-components/LLButton'
import LLPopper from '@/components/utils/LLPopper'
import LLTextareaMenuBar from '@/components/common/LLTextarea/LLTextareaMenuBar'
import LLTextareaEmoji from '@/components/common/LLTextarea/LLTextareaEmoji'
import EmojiIcon from '@/assets/icons/EmojiIcon'
import LLInputError from '@/components/common/LLInput/LLInputError'
import LLInputCaption from '@/components/common/LLInput/LLInputCaption'
import EnterHardBreak from '@/utils/tiptap/enterHardBreak'
import { screenCatcher } from '@/mixins/screenCatcher'
import mentionToHtml from '@/utils/mentionToHtml'
import ChevronDownIcon from '@/assets/icons/ChevronDownIcon'

export default {
  name: 'LLTextarea',
  $_veeValidate: {
    // fetch the current value from the innerValue defined in the component data.
    value() {
      return this.text
    }
  },
  components: {
    TextareaMenuBarIcon,
    LLInputCaption,
    LLInputError,
    EmojiIcon,
    LLTextareaEmoji,
    LLTextareaMenuBar,
    LLPopper,
    LLButton,
    ChevronDownIcon,
    EditorContent
  },
  mixins: [screenCatcher],
  props: {
    errors: { type: Array, default: () => [] },
    /**
     * @type {string|number}
     * @enum ['normal', 'compact', 'any']
     */
    size: { type: String, Number, default: 'normal' },
    /**
     * @type {string}
     * @enum ['compact', 'normal', 'absolute', 'below']
     * 'below' renders Aa/emoji (and the `additional-actions` slot) as one full-width row
     * under the text instead of squeezed beside it - see #1684.
     */
    menuBarStyle: { type: String, default: 'compact' },
    disabled: { type: Boolean, default: false },
    dynamicSize: { type: Boolean, default: false },
    maxHeight: { type: Number, default: null },
    hideMenuBar: { type: Boolean, default: false },
    hideEmoji: { type: Boolean, default: false },
    value: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    showErrorInTooltip: { type: Boolean, default: false },
    hardBreakOnEnter: { type: Boolean, default: false },
    allowLists: { type: Boolean, default: true },
    plainText: { type: Boolean, default: false },
    maxLength: { type: Number, default: null },
    mentionSuggestionComponent: { type: Object, default: null },
    mentionSuggestionProps: { type: Object, default: null },
    // Emits `submit` on Ctrl+Enter / Cmd+Enter (never while an IME composition is in
    // progress), so Enter itself always stays a plain newline. See #1684 requirement 7.
    submitOnModEnter: { type: Boolean, default: false },
    // Shown in the mobile fullscreen editor's header, next to Collapse - e.g. "Your
    // response" / "Your reply" - matches the prototype's fullscreen title - see #1684.
    expandedTitle: { type: String, default: '' },
    // Smaller context line under expandedTitle - e.g. the question text being answered,
    // or "Chat thread" - matches the prototype's fullscreen subtitle - see #1684.
    expandedSubtitle: { type: String, default: '' }
  },
  data() {
    return {
      text: '',
      editor: null,
      editorStates: {
        isFocused: false
      },
      showError: false,
      showCaption: false,
      showBottomContent: false,
      showAdditionalActions: false,
      // Snapshot of the last accepted content, used to hard-limit plain-text input.
      lastAcceptedContent: '',
      // Last plain-text value emitted, so the v-model echo does not reset the editor.
      lastEmittedPlain: null,
      menuStates: {
        emojiTop: false,
        emoji: false,
        menuBar: false
      },
      // 'below' composer mode only (#1684): is the content currently overflowing the
      // viewport-relative height cap? Drives the mobile "Expand" control.
      isCapped: false,
      // Mobile fullscreen editor, opened from "Expand" / closed via "Collapse" - see #1684
      // requirement 9. Same editor instance, just repositioned by CSS, so no text copy/sync.
      isExpanded: false,
      // Desktop manual drag-resize (optional per #1684); null lets the CSS viewport-based cap apply.
      manualMaxHeight: null,
      isResizing: false,
      resizeStartY: 0,
      resizeStartHeight: 0
    }
  },
  computed: {
    filled() {
      return this.text !== '<p></p>' && this.text?.length
    },
    hasError() {
      return !!this.errors?.length
    },
    hasAnyAction() {
      return !this.hideMenuBar || !this.hideEmoji || (this.showAdditionalActions && this.hideMenuBar)
    },
    isActionsInline() {
      return this.hasAnyAction && this.menuBarStyle !== 'below'
    },
    isActionsBelow() {
      return this.hasAnyAction && this.menuBarStyle === 'below'
    },
    editorWrapperStyle() {
      // `height`, not just `max-height`: max-height only caps growth, it doesn't force the
      // box to expand when the content is shorter than the dragged size - a manual drag
      // needs to behave like a native textarea's resize handle and actually grow the box,
      // same as it would with more content - see #1684.
      return this.manualMaxHeight
        ? { height: `${this.manualMaxHeight}px`, maxHeight: `${this.manualMaxHeight}px` }
        : {}
    },
    // Matches the CSS min-height for the 'below' composer mode (3 lines mobile / 4 desktop
    // at 16px/1.5 line-height) - the floor for the desktop manual drag-resize handle.
    minEditorHeight() {
      // +10px matches the CSS min-height compensation for this element's own padding-top -
      // see #1684.
      return (this.tailwindScreens.sm ? 96 : 72) + 10
    },
    errorsString() {
      return this.hasError ? this.errors.join(', ') : null
    },
    inputStyle() {
      const style = {}
      // 'below' composer mode sizes itself via CSS (viewport-relative cap, see styles) -
      // the generic size/maxHeight logic below would otherwise fight that - see #1684.
      if (this.isActionsBelow) {
        return style
      }
      let height
      if (isNumber(this.size)) {
        height = `${this.size}px`
      } else if (this.size === 'normal') {
        height = '122px'
      } else if (this.size === 'compact') {
        height = '46px'
      }
      if (this.maxHeight) {
        style.maxHeight = `${this.maxHeight}px`
      }

      if (this.dynamicSize) {
        style.minHeight = height
      } else {
        style.height = height
      }
      return style
    },
    inputClasses() {
      const classes = []
      const rootClass = 'LLTextarea'
      if (this.size === 'normal') {
        classes.push(`${rootClass}_size-normal`)
      } else if (this.size === 'compact') {
        classes.push(`${rootClass}_size-compact`)
      }
      if (this.menuBarStyle === 'absolute') {
        if (this.tailwindScreens.sm) {
          classes.push(`${rootClass}_menu-bar-absolute`)
        } else {
          classes.push(`${rootClass}_menu-bar-compact`)
        }
      } else if (this.menuBarStyle === 'compact') {
        classes.push(`${rootClass}_menu-bar-compact`)
      } else if (this.menuBarStyle === 'normal') {
        classes.push(`${rootClass}_menu-bar-normal`)
      } else if (this.menuBarStyle === 'below') {
        classes.push(`${rootClass}_menu-bar-below`)
      }
      if (this.editorStates.isFocused) {
        classes.push(`${rootClass}_focused`)
      }
      if (this.disabled) {
        classes.push(`${rootClass}_disabled`)
      }
      if (this.hasError) {
        classes.push(`${rootClass}_error`)
      }
      if (this.filled) {
        classes.push(`${rootClass}_filled`)
      }
      if (this.isExpanded) {
        classes.push(`${rootClass}_expanded`)
      }
      return classes
    },
    hasContent() {
      return this.text?.length
    }
  },
  watch: {
    text: {
      handler(text) {
        const newText = this.textReplacer(text)
        if (this.editor && newText !== this.editor.getHTML()) {
          this.editor.commands.setContent(newText, true, {
            preserveWhitespace: 'full'
          })
        }
        if (this.editor) this.lastAcceptedContent = this.editor.getHTML()
      }
    },
    value: {
      handler() {
        if (this.plainText) {
          // Skip the echo of our own emission: re-setting the content would move the caret.
          if (this.value === this.lastEmittedPlain) return
          // External (e.g. server prefill) value arrives as plain text.
          this.lastEmittedPlain = null
          this.text = this.plainToHtml(this.value)
          return
        }
        this.text = emojiToHtml.img(this.value)
        this.text = mentionToHtml.tiptap(this.value)
      },
      immediate: true
    },
    placeholder() {
      this.setPlaceholder()
    }
  },
  created() {
    this.setShowSlots()
  },
  beforeUpdate() {
    this.setShowSlots()
  },
  mounted() {
    const formattingExtensions = this.plainText ? [] : [Bold, Italic, Underline, Link]
    const extensions = [
      EmojiImage.configure({
        inline: true,
        HTMLAttributes: {
          class: 'emoji'
        }
      }),
      new Mention({
        mentionSuggestionComponent: this.mentionSuggestionComponent,
        mentionSuggestionProps: this.mentionSuggestionProps
      }),
      Text,
      ...formattingExtensions,
      Document,
      HardBreak,
      Paragraph,
      Placeholder.configure({
        placeholder: () => {
          return this.placeholder
        },
        showOnlyWhenEditable: false,
        showOnlyCurrent: true
      })
    ]
    if (this.allowLists) {
      extensions.push(BulletList, OrderedList, ListItem)
    }
    if (this.hardBreakOnEnter) {
      extensions.push(EnterHardBreak)
    }
    this.editor = new Editor({
      // Tiptap parses the initial content as HTML; plain-text callers pass plain text.
      content: this.plainText ? this.plainToHtml(this.value) : this.value,
      editable: !this.disabled,
      extensions,
      onUpdate: (event) => {
        const content = this.editor.getHTML()
        const newText = this.textReplacer(content)
        if (newText !== content && content !== '<p></p>') {
          this.editor.commands.setContent(newText, true, {
            preserveWhitespace: 'full'
          })
        }
        const html = this.htmlToMention(this.htmlToEmoji(newText))
        // Plain-text mode emits the visible text (no tags), which is what the API expects.
        // The editor still keeps HTML internally so its content stays in sync.
        if (this.plainText) {
          const plain = this.getPlainText()
          if (this.maxLength && plain.length > this.maxLength) {
            // Hard limit: roll back to the last accepted content. `emitUpdate = false`
            // avoids re-entering onUpdate (which could recurse when the snapshot is
            // itself over the limit).
            this.editor.commands.setContent(this.lastAcceptedContent, false, {
              preserveWhitespace: 'full'
            })
            return
          }
          this.lastAcceptedContent = this.editor.getHTML()
          this.lastEmittedPlain = plain
          this.text = html
          this.$emit('input', plain, event)
          return
        }
        this.text = html
        this.$emit('input', this.text, event)
        this.checkCapped()
      },
      onFocus: () => {
        if (!this.disabled) {
          this.onFocus()
        }
      },
      onBlur: () => {
        this.onBlur()
      }
    })
    this.setPlaceholder()
    this.lastAcceptedContent = this.editor.getHTML()
    if (this.submitOnModEnter) {
      this.editor.view.dom.addEventListener('keydown', this.onModEnterKeydown)
    }
    if (this.isActionsBelow && this.dynamicSize) {
      window.addEventListener('resize', this.checkCapped)
      this.$nextTick(() => this.checkCapped())
    }
    if (this.isActionsBelow && window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.keepAboveKeyboard)
      window.visualViewport.addEventListener('scroll', this.keepAboveKeyboard)
    }
  },
  beforeDestroy() {
    if (this.submitOnModEnter && this.editor?.view?.dom) {
      this.editor.view.dom.removeEventListener('keydown', this.onModEnterKeydown)
    }
    window.removeEventListener('resize', this.checkCapped)
    window.removeEventListener('mousemove', this.onResizeMove)
    window.removeEventListener('mouseup', this.onResizeEnd)
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.keepAboveKeyboard)
      window.visualViewport.removeEventListener('scroll', this.keepAboveKeyboard)
    }
    if (this.editor && this.editor?.destroy) {
      this.editor.destroy()
    }
  },
  methods: {
    // Plain-text mode: the editor works with HTML internally, callers pass/read plain text.
    // Escaping keeps HTML-like input from being parsed as markup; `<br>` keeps line breaks.
    plainToHtml(text) {
      if (text == null || text === '') return ''
      const escaped = String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      return `<p>${escaped.replace(/\n/g, '<br>')}</p>`
    },
    // Hard breaks already serialize to '\n' (HardBreak.renderText); use '\n' between
    // blocks too so the round-trip with `plainToHtml` is lossless.
    getPlainText() {
      return this.editor.getText({ blockSeparator: '\n' })
    },
    htmlToEmoji(htmlContent) {
      const result = htmlContent.replace(
        /<img src="[\w/.]+" class="emoji-img" emojicode="[\w\d+]+">/gi,
        (match) => {
          const emojiCodeList = [...match.matchAll(/emojicode="([\w\d+]+)"/g)]?.[0]?.[1]?.split('+') || []
          return String.fromCodePoint(...emojiCodeList.map((code) => parseInt(code, 16)))
        }
      )
      return result
    },
    htmlToMention(htmlContent) {
      const result = htmlContent.replaceAll(/<span class="mention"([^>]*)>([^<]+)<\/span>/gi, (match) => {
        const prospectId = [...match.matchAll(/prospect-id="([^"]+)"/gi)]?.[0]?.[1]
        const userId = [...match.matchAll(/user-id="([^"]+)"/gi)]?.[0]?.[1]
        const fullName = [...match.matchAll(/full-name="([^"]+)"/gi)]?.[0]?.[1]
        const mentionId = [...match.matchAll(/mention-id="([^"]+)"/gi)]?.[0]?.[1]
        const isReply = [...match.matchAll(/is-reply="([^"]+)"/gi)]?.[0]?.[1]
        const isAnonymous = [...match.matchAll(/is-anonymous="([^"]+)"/gi)]?.[0]?.[1]
        const toModerator = [...match.matchAll(/to-moderator="([^"]+)"/gi)]?.[0]?.[1]
        const anonymousId = [...match.matchAll(/anonymous-id="([^"]+)"/gi)]?.[0]?.[1]
        return `{{mention:${JSON.stringify(
          JSON.stringify({
            prospectId,
            userId,
            fullName,
            mentionId,
            toModerator: !(!toModerator || toModerator === 'false'),
            isReply: !(!isReply || isReply === 'false'),
            isAnonymous: !(!isAnonymous || isAnonymous === 'false'),
            anonymousId
          })
        )}}}`
      })
      return result
    },
    textReplacer(content) {
      if (content === '<p></p>') {
        return ''
      }
      const newContent = mentionToHtml.tiptap(emojiToHtml.img(content))
      return newContent
    },
    setPlaceholder() {
      this.editor.commands.resetAttributes('placeholder')
    },
    onActionsClick(e) {
      if (
        !e.composedPath().find((el) => el?.classList?.contains('LLTextarea__action')) &&
        this.editor?.focus
      ) {
        this.editor.commands.focus('end')
      } else {
        this.editor.commands.focus()
      }
    },
    setShowSlots() {
      this.$nextTick(() => {
        this.showError = !!this.$slots?.error?.[0]
        this.showCaption = !!this.$slots?.caption?.[0]
        this.showBottomContent = !!this.$slots?.['bottom-content']?.[0]
        this.showAdditionalActions = !!this.$slots?.['additional-actions']?.[0]
      })
    },
    insertText(text) {
      this.editor.commands.insertContent(text, {
        parseOptions: {
          preserveWhitespace: 'full'
        }
      })
    },
    setContent(text) {
      this.editor.commands.setContent(text, {
        parseOptions: {
          preserveWhitespace: 'full'
        }
      })
    },
    onEmojiSelect(emoji) {
      this.editor.commands.setEmoji({ emoji: emoji.emoji })
      this.editor.commands.focus()
    },
    insertMention({ prospectId, userId, fullName, isReply, isAnonymous, toModerator, anonymousId }) {
      this.editor.commands.setMention({
        options: {
          anonymousId,
          prospectId,
          userId,
          fullName,
          isReply,
          isAnonymous,
          toModerator
        }
      })
      this.editor.commands.focus()
      this.editor.commands.insertContent(' ', {
        parseOptions: {
          preserveWhitespace: 'full'
        }
      })
    },
    focus() {
      if (this.editor.commands) {
        this.editor.commands.focus()
      }
    },
    onFocus() {
      this.editorStates.isFocused = true
      this.$emit('focus')
      // The on-screen keyboard's open animation finishes after focus fires, so check
      // again once it has settled - see #1684 requirement 11.
      if (this.isActionsBelow) {
        setTimeout(this.keepAboveKeyboard, 350)
      }
    },
    onBlur() {
      this.editorStates.isFocused = false
      this.$emit('blur')
    },
    // Mobile on-screen keyboard shrinks (or scrolls) the visual viewport - without this,
    // a focused composer near the bottom of a long page can end up hidden behind the
    // keyboard. Nudges the page (not the composer, which is inline, not fixed) so the
    // composer's bottom edge stays above the keyboard - see #1684 requirement 11.
    keepAboveKeyboard() {
      if (!this.editorStates.isFocused || this.tailwindScreens.sm || !window.visualViewport) return
      const el = this.$el
      if (!el) return
      const rect = el.getBoundingClientRect()
      const vv = window.visualViewport
      const visibleBottom = vv.height + vv.offsetTop
      if (rect.bottom > visibleBottom) {
        window.scrollBy({ top: rect.bottom - visibleBottom, left: 0, behavior: 'smooth' })
      }
    },
    onModEnterKeydown(event) {
      // event.isComposing guards against IME users (Chinese/Japanese/Korean) losing a
      // half-composed character to the shortcut - see #1684 requirement 7.
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && !event.isComposing) {
        event.preventDefault()
        this.$emit('submit')
      }
    },
    // 'below' composer mode (#1684): is the editor currently taller than its viewport-relative
    // cap (or manual drag height)? Drives the mobile "Expand" control shown via the
    // `additional-actions` scoped slot.
    checkCapped() {
      if (this.isExpanded) {
        this.isCapped = false
        return
      }
      this.$nextTick(() => {
        const el = this.$refs.editorWrapper
        this.isCapped = !!el && el.scrollHeight - el.clientHeight > 1
      })
    },
    expand() {
      if (!this.isCapped) return
      this.isExpanded = true
      this.isCapped = false
      this.$nextTick(() => {
        this.editor?.commands?.focus('end')
      })
    },
    collapse() {
      this.isExpanded = false
      this.$nextTick(() => this.checkCapped())
    },
    onResizeStart(event) {
      // window-level mousemove/mouseup: Pointer Events + setPointerCapture were tried here
      // but pointerdown doesn't reliably fire in this stack - reverted to the
      // mouse-event approach, which does - see #1684.
      event.preventDefault()
      const el = this.$refs.editorWrapper
      if (!el) return
      this.isResizing = true
      this.resizeStartY = event.clientY
      this.resizeStartHeight = el.getBoundingClientRect().height
      window.addEventListener('mousemove', this.onResizeMove)
      window.addEventListener('mouseup', this.onResizeEnd)
    },
    onResizeMove(event) {
      if (!this.isResizing) return
      const delta = event.clientY - this.resizeStartY
      this.manualMaxHeight = Math.max(this.resizeStartHeight + delta, this.minEditorHeight)
      this.checkCapped()
    },
    onResizeEnd() {
      this.isResizing = false
      window.removeEventListener('mousemove', this.onResizeMove)
      window.removeEventListener('mouseup', this.onResizeEnd)
    }
  }
}
</script>

<style lang="scss" scoped>
.LLTextarea {
  $root: &;
  min-width: 0;
  min-height: 0;
  @apply relative;

  &_menu-bar-compact {
    #{$root}__header {
      @apply hidden;
    }
  }
  &_menu-bar-absolute {
    #{$root}__header {
      @apply absolute top-0 right-0;
      margin-top: -34px;
    }
    #{$root}__actions {
      @apply hidden;
    }
  }
  &_menu-bar-normal {
    #{$root}__header {
      @apply w-full;
    }
    #{$root}__actions {
      @apply hidden;
    }
  }
  &_menu-bar-below {
    #{$root}__header {
      @apply hidden;
    }
    #{$root}__input-content {
      @apply relative;
    }
    #{$root}__editor-wrapper {
      // 12px horizontal inset (not the generic 16px) and no bottom padding of its own -
      // the gap down to the toolbar comes from the toolbar's own top padding instead,
      // matching the prototype's single shared composer padding exactly - see #1684.
      @apply px-3 pt-2.5 pb-0;
      // +10px compensates for this element's own pt-2.5 padding-top (box-sizing: border-box
      // counts it against min/max-height) - the prototype's padding sits outside the
      // textarea, so its 72px/96px is pure content height; see #1684.
      min-height: calc(72px + 10px); // ~3 lines at 16px/1.5 - mobile baseline
      max-height: calc(40vh + 10px);
    }
    // The toolbar row is inset uniformly (12px = the box's gutter, same as the side padding
    // of the editor above and the divider's left/right inset), so the controls - Send in
    // particular - keep equal spacing on all sides: below the divider, above the box's
    // bottom border and from the side borders - #1684 QA.
    #{$root}__actions_below {
      @apply p-3 gap-0.5 flex-wrap;
      // Controls keep their full 44px touch target instead of being squished when the row
      // runs out of room (e.g. Chat on a phone once the mobile "Expand" control appears);
      // the row wraps instead, so Send never collapses to a sliver or spills past the box - #1684 QA.
      ::v-deep > * {
        flex-shrink: 0;
      }
      // When the row wraps, the primary action (Send) lands on its own line - keep it
      // right-aligned there, mirroring its position in the single-row layout. On one row
      // this is a no-op: the caller's spacer already absorbs the free space - #1684 QA.
      ::v-deep .LLButton_primary {
        margin-left: auto;
      }
    }
    #{$root}__hint {
      @apply px-3 pt-1.5 pb-2;
    }
    #{$root}__resize-handle_below {
      bottom: 8px; // matches the toolbar's own 8px margin-top in the prototype, see #1684
    }
    #{$root}__additional-content_above {
      @apply px-3 pt-2 pb-0 flex flex-wrap gap-2;
    }
    @screen sm {
      #{$root}__editor-wrapper {
        min-height: calc(96px + 10px); // ~4 lines - desktop baseline, see #1684
        max-height: calc(45vh + 10px);
      }
    }
  }
  &_menu-bar-below#{&}_focused {
    #{$root}__input-wrapper {
      // The app's "primary" navy (#1f4e6e) reads as dark, not as a focus accent - the
      // palette has no brighter blue token, so this matches the prototype's focus color
      // (#2f7fb8) directly, scoped to just this composer - see #1684.
      border-color: #2f7fb8;
      box-shadow: 0 0 0 3px rgba(47, 127, 184, 0.18);
    }
  }
  // Mobile fullscreen "Expand" (#1684 requirement 9): same editor instance, just
  // repositioned to cover the viewport, so text is never copied/lost between modes.
  &_expanded {
    position: fixed;
    inset: 0;
    z-index: 1001;
    background: white;
    display: flex;
    flex-direction: column;
    border-radius: 0;
    #{$root}__container {
      @apply flex flex-col flex-1 h-full;
      min-height: 0;
    }
    #{$root}__input-wrapper {
      @apply flex-1 border-0;
      min-height: 0;
      border-radius: 0;
    }
    #{$root}__input-content {
      @apply h-full;
    }
    #{$root}__editor-wrapper {
      max-height: none !important;
      min-height: 0 !important;
    }
  }
  &_size-compact {
    #{$root}__input-wrapper {
      height: auto;
    }
  }

  &__header {
    @apply flex justify-end;
  }
  &_error {
    #{$root}__input-wrapper {
      @apply border-status-03-600;
    }
  }
  &_focused {
    #{$root}__input-wrapper {
      @apply border-neutral-01-700;
    }
  }
  &_disabled {
    #{$root}__input-wrapper {
      @apply border-neutral-01-100 bg-neutral-01-15 text-neutral-01-400;
    }
    @apply pointer-events-none;
  }
  &_error {
    #{$root}__input-wrapper {
      @apply border-status-03-600;
    }
  }
  &:hover:not(#{$root}_disabled):not(#{$root}_error):not(#{$root}_focused):not(#{$root}_menu-bar-below) {
    #{$root}__input-wrapper {
      @apply border-neutral-01-400;
    }
  }
  &__editor {
    min-height: 20px !important;
    max-width: 100% !important;
    height: 100%;
  }
  &__editor-content {
    @apply w-full;
  }
  &__container {
    @apply relative h-full;
    min-width: 0;
    min-height: 0;
  }
  &__input-wrapper {
    @apply border rounded transition duration-200  w-full border-neutral-01-75 h-full bg-white relative flex flex-col;
    min-height: 46px;
  }
  &__input-content {
    @apply w-full flex h-full min-w-0;
  }
  &__editor-wrapper {
    @apply flex-1 min-w-0 overflow-y-auto;
    @apply px-4 py-2.5;
  }
  &__actions {
    @apply flex-shrink-0 pb-1.5 pr-1.5 flex items-end;
    // The toolbar row under the text, full width, in its own line below the editor
    // (and below any file chips). Only the icon buttons here need to stay full size
    // (44px, see LLButton's default/round sizing) for touch targets - see #1684.
    // The divider is an inset line (matching the textarea's own horizontal padding),
    // not a full-bleed border - mirrors the composer prototype exactly - see #1684.
    &_below {
      @apply w-full flex-shrink flex items-center gap-1 px-2 py-1 relative;
      &::before {
        content: '';
        @apply absolute top-0 left-3 right-3 border-t border-neutral-01-75;
      }
    }
  }
  &__additional-content {
    @apply w-full px-4 pb-4;
  }
  &__popup-menubar {
    @apply p-1;
  }
  // Desktop-only manual drag-resize handle for the 'below' composer mode (#1684 suggestion).
  // Contenteditable can't use the native textarea `resize: vertical`, so this is a small
  // custom grip wired to JS instead.
  &__resize-handle {
    // Bigger than the visible 10px glyph - a 16px hit target is easy to miss with a real
    // mouse (works fine via the precise coordinates a test script clicks) - see #1684.
    @apply hidden items-center justify-center absolute bottom-0 right-0 w-6 h-6 text-neutral-01-400;
    cursor: ns-resize;
    touch-action: none;
    user-select: none;
    @screen sm {
      @apply flex;
    }
    &:hover {
      @apply text-neutral-01-700;
    }
  }
  // Desktop-only hint under the toolbar for the 'below' composer mode - see #1684.
  &__hint {
    @apply hidden px-4 pb-2 text-caption-02 text-neutral-01-400;
    @screen sm {
      @apply block;
    }
  }
  // "Aa" formatting toggle, as literal text (matching the prototype's <span class="aa">Aa</span>)
  // rather than a vector icon glyph - see #1684.
  &__aa-label {
    font-size: 15px;
    font-weight: 600;
    line-height: 1;
  }
  // Title (left) + Collapse pill (right), with a divider below - matches the prototype's
  // fullscreen header (.fs-head) exactly, not just a bare right-aligned button - #1684.
  &__expanded-header {
    @apply flex items-center justify-between px-4 py-3.5 border-b border-neutral-01-50;
  }
  &__expanded-header-text {
    @apply min-w-0;
  }
  &__expanded-title {
    @apply text-button-01 text-primary-01-800 block truncate;
  }
  &__expanded-subtitle {
    @apply text-caption-01 text-neutral-01-400 block truncate mt-0.5;
  }
  &__collapse-button {
    @apply rounded-lg;
  }

  ::v-deep .ProseMirror {
    * {
      font-family: 'Open Sans', 'Arial', 'Noto Color Emoji', 'Noto Emoji', sans-serif;
    }
    height: 100%;
    // iOS Safari zooms the page on focus if the field's font-size is under 16px - #1684.
    font-size: 16px;
    &-focused,
    &:focus {
      @apply outline-none border-neutral-01;
      .is-disabled & {
        @apply outline-none border-neutral-01-15;
      }
    }

    /*.has-errors & {
      @apply border-error-01;
    }*/

    .mention {
      @apply text-accent-01;
    }

    .is-empty {
      @apply text-neutral-01-400;
    }

    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      pointer-events: none;
      height: 0;
    }

    ol,
    ul {
      list-style: initial;
      @apply pl-6 my-2;
    }

    a {
      @apply underline cursor-pointer text-primary-01-400;
    }
  }
}
</style>
